import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';

import { EventDateRange } from '@/apis/eventDateRange';
import { EventTypeRecord } from '@/apis/eventTypes';
import { AutocompleteResult, TemplatePlaceholder } from '@/types';
import { Box } from '@mui/material';
import { EventAttendance } from '@pages/QuickFilterListCreatorPage/criteriaComponents/EventAttendance';
import {
  EventParticipantRecordResult,
  EventRecordResult,
} from '@/types/filterCriteriaRecords';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';

import { useAdvancedSearchDateFilterInvalid } from './useAdvancedSearchDateFilterInvalid';

export type EventAttendanceWidgetValue = {
  statuses: EventParticipantRecordResult[] | TemplatePlaceholder;
  eventTypes: EventTypeRecord[] | TemplatePlaceholder;
  eventDateRange: EventDateRange;
  events: EventRecordResult[] | TemplatePlaceholder;
  errors: MappingError[];
  eventCampaigns: AutocompleteResult[] | TemplatePlaceholder;
};

type CombinedProps = BaseWidgetProps<Config, EventAttendanceWidgetValue>;

const defaultFilterState: EventAttendanceWidgetValue = {
  statuses: [],
  eventTypes: [],
  eventDateRange: { startDate: null, endDate: null },
  events: [],
  errors: [],
  eventCampaigns: [],
};

const EventAttendanceWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
}: CombinedProps) => {
  const setIsEventDateFilterInvalid = useAdvancedSearchDateFilterInvalid();
  const handleChangeEventStatuses = (
    newValue: EventParticipantRecordResult[] | TemplatePlaceholder
  ) => {
    setValue({
      ...(value ?? defaultFilterState),
      statuses: newValue,
      errors: [],
    });
  };

  const handleChangeEventTypes = (newValue: EventTypeRecord[] | TemplatePlaceholder) => {
    setValue({ ...(value ?? defaultFilterState), eventTypes: newValue, errors: [] });
  };

  const handleChangeEventDateRange = (newValue: EventDateRange) => {
    setValue({ ...(value ?? defaultFilterState), eventDateRange: newValue, errors: [] });
  };

  const handleChangeEvents = (newValue: EventRecordResult[] | TemplatePlaceholder) => {
    setValue({ ...(value ?? defaultFilterState), events: newValue, errors: [] });
  };

  const handleChangeCampaigns = (newValue: AutocompleteResult[] | TemplatePlaceholder) => {
    setValue({ ...(value ?? defaultFilterState), eventCampaigns: newValue, errors: [] });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: "column",
        gap: 3,
        minWidth: '40ch'
      }}>
      <EventAttendance
        eventStatuses={value?.statuses ?? []}
        onChangeEventStatuses={(newValue) => handleChangeEventStatuses(newValue)}
        eventTypes={value?.eventTypes ?? []}
        onChangeEventTypes={handleChangeEventTypes}
        eventDateRange={value?.eventDateRange ?? { endDate: null, startDate: null }}
        onChangeEventDateRange={handleChangeEventDateRange}
        events={value?.events ?? []}
        onChangeEvents={handleChangeEvents}
        campaigns={value?.eventCampaigns ?? []}
        onChangeCampaigns={handleChangeCampaigns}
        setIsEventDateFilterInvalid={setIsEventDateFilterInvalid}
        isAdvancedSearch
        errors={value?.errors}
      />
    </Box>
  );
};

export const eventAttendanceWidget: BaseWidget = {
  type: 'eventAttendance',
  hideOperator: true,
  operatorInlineLabel: 'has',
  factory: EventAttendanceWidgetFactory,
};
